import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, IconButton, Chip, useTheme, useMediaQuery } from '@mui/material';
import { Refresh as RefreshIcon, Schedule as ScheduleIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import theme from '../../styles/theme';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

/**
 * LastUpdated Component
 * Displays the timestamp of the last data refresh with Material-UI design
 * Features:
 * - Human-readable timestamp formatting with timezone support
 * - Position in bottom-right corner with responsive design
 * - Optional refresh icon with manual refresh capability
 * - Subtle animations using framer-motion
 * - Integration with existing theme system
 * - Support for different timestamp formats
 */
const LastUpdated = ({
  timestamp = null,
  onRefresh = null,
  loading = false,
  position = 'bottom-right',
  showRelativeTime = true,
  showRefreshButton = true,
  variant = 'default' // 'default', 'compact', 'inline'
}) => {
  const muiTheme = useTheme();
  const [displayTime, setDisplayTime] = useState('');
  const [relativeTimeText, setRelativeTimeText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Responsive breakpoints
  const isLargeScreen = useMediaQuery(muiTheme.breakpoints.up('lg'));
  const is4K = useMediaQuery('(min-width: 2560px)');

  // Format timestamp with proper timezone handling
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    
    try {
      const date = dayjs(timestamp);
      
      // Handle different timestamp formats
      if (!date.isValid()) {
        // Try parsing as Unix timestamp
        const unixDate = dayjs.unix(timestamp);
        if (unixDate.isValid()) {
          return formatValidDate(unixDate);
        }
        return 'Invalid date';
      }
      
      return formatValidDate(date);
    } catch (error) {
      console.warn('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  const formatValidDate = (date) => {
    // Convert to Europe/Rome timezone (CET/CEST)
    const localDate = date.tz('Europe/Rome');
    
    // Format: "3 Feb 2025, 14:32 CET"
    return localDate.format('D MMM YYYY, HH:mm z');
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = dayjs(timestamp);
      if (!date.isValid()) return '';
      
      const now = dayjs();
      const diffMinutes = now.diff(date, 'minute');
      
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      
      const diffHours = now.diff(date, 'hour');
      if (diffHours < 24) return `${diffHours}h ago`;
      
      const diffDays = now.diff(date, 'day');
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.tz('Europe/Rome').format('D MMM');
    } catch (error) {
      return '';
    }
  };

  // Update display time when timestamp changes
  useEffect(() => {
    const newDisplayTime = formatTimestamp(timestamp);
    const newRelativeTime = formatRelativeTime(timestamp);
    
    if (newDisplayTime !== displayTime) {
      setIsAnimating(true);
      setDisplayTime(newDisplayTime);
      setRelativeTimeText(newRelativeTime);
      
      // Reset animation state
      setTimeout(() => setIsAnimating(false), 300);
    }
  }, [timestamp, displayTime]);

  // Update relative time every minute
  useEffect(() => {
    if (!timestamp || !showRelativeTime) return;
    
    const interval = setInterval(() => {
      setRelativeTimeText(formatRelativeTime(timestamp));
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [timestamp, showRelativeTime]);

  const handleRefresh = () => {
    if (onRefresh && !loading) {
      setIsAnimating(true);
      onRefresh();
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  // Position styles
  const getPositionStyles = () => {
    const baseSpacing = is4K ? 32 : isLargeScreen ? 24 : 16;
    
    const positions = {
      'bottom-right': {
        position: 'fixed',
        bottom: baseSpacing,
        right: baseSpacing,
        zIndex: 1300
      },
      'bottom-left': {
        position: 'fixed',
        bottom: baseSpacing,
        left: baseSpacing,
        zIndex: 1300
      },
      'top-right': {
        position: 'fixed',
        top: baseSpacing,
        right: baseSpacing,
        zIndex: 1300
      },
      'top-left': {
        position: 'fixed',
        top: baseSpacing,
        left: baseSpacing,
        zIndex: 1300
      },
      'inline': {
        position: 'relative'
      }
    };
    
    return positions[position] || positions['bottom-right'];
  };

  // Variant styles
  const getVariantStyles = () => {
    const fontSize = is4K ? theme.typography.fontSize.base : theme.typography.fontSize.sm;
    
    const variants = {
      default: {
        padding: is4K ? theme.spacing[4] : theme.spacing[3],
        borderRadius: theme.borderRadius.lg,
        minWidth: is4K ? '280px' : '240px'
      },
      compact: {
        padding: is4K ? theme.spacing[3] : theme.spacing[2],
        borderRadius: theme.borderRadius.md,
        minWidth: is4K ? '200px' : '180px'
      },
      inline: {
        padding: theme.spacing[2],
        borderRadius: theme.borderRadius.base,
        minWidth: 'auto'
      }
    };
    
    return {
      ...variants[variant],
      fontSize
    };
  };

  const positionStyles = getPositionStyles();
  const variantStyles = getVariantStyles();

  // Animation variants
  const containerVariants = {
    initial: { opacity: 0, scale: 0.9, y: 10 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.9, y: -10 }
  };

  const textVariants = {
    initial: { opacity: 0, x: -10 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: 10 }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={positionStyles}
    >
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0)',
          // backdropFilter: 'blur(8px)',
          // border: `1px solid ${theme.colors.neutral[200]}`,
          // boxShadow: theme.shadows.lg,
          display: 'flex',
          alignItems: 'center',
          gap: variant === 'compact' ? 1 : 1.5,
          ...variantStyles,
          // '&:hover': {
          //   backgroundColor: 'rgba(255, 255, 255, 0)',
          //   boxShadow: theme.shadows.xl,
          // }
        }}
      >
        {/* Clock Icon */}
        <ScheduleIcon 
          sx={{ 
            color: theme.colors.primary[50],
            fontSize: is4K ? '20px' : '16px'
          }} 
        />

        {/* Timestamp Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={displayTime}
              variants={textVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.2 }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: theme.colors.text.inverse,
                  fontWeight: theme.typography.fontWeight.medium,
                  fontSize: variantStyles.fontSize,
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap'
                }}
              >
                Last updated: {displayTime}
              </Typography>
              
              {showRelativeTime && relativeTimeText && variant !== 'compact' && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.colors.text.muted,
                    fontSize: is4K ? theme.typography.fontSize.sm : theme.typography.fontSize.xs,
                    display: 'block',
                    mt: 0.25
                  }}
                >
                  {relativeTimeText}
                </Typography>
              )}
            </motion.div>
          </AnimatePresence>
        </Box>

        {/* Refresh Button */}
        {showRefreshButton && onRefresh && (
          <motion.div
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              size="small"
              sx={{
                color: loading ? theme.colors.neutral[400] : theme.colors.primary[500],
                '&:hover': {
                  backgroundColor: theme.colors.primary[50],
                },
                '&:disabled': {
                  color: theme.colors.neutral[300]
                }
              }}
              title="Refresh data"
            >
              <motion.div
                animate={loading || isAnimating ? { rotate: 360 } : { rotate: 0 }}
                transition={{ 
                  duration: loading ? 1 : 0.3, 
                  repeat: loading ? Infinity : 0,
                  ease: "linear"
                }}
              >
                <RefreshIcon sx={{ fontSize: is4K ? '20px' : '16px' }} />
              </motion.div>
            </IconButton>
          </motion.div>
        )}

        {/* Status Indicator */}
        {/* {timestamp && (
          <Chip
            size="small"
            label="Live"
            sx={{
              height: is4K ? '24px' : '20px',
              fontSize: is4K ? theme.typography.fontSize.xs : '10px',
              backgroundColor: theme.colors.success,
              color: 'white',
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />
        )} */}
      </Box>
    </motion.div>
  );
};

LastUpdated.propTypes = {
  timestamp: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date)
  ]),
  onRefresh: PropTypes.func,
  loading: PropTypes.bool,
  position: PropTypes.oneOf([
    'bottom-right', 
    'bottom-left', 
    'top-right', 
    'top-left', 
    'inline'
  ]),
  showRelativeTime: PropTypes.bool,
  showRefreshButton: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'compact', 'inline'])
};


export default LastUpdated;